import { useState } from "react";

import { fillSignedConsentPdf } from "@libs/pdf";
import { useRecordDownloadEventMutation } from "@/data/mutations/auditTrailsMutations";
import {
  getRepresentativeName,
  SignedConsentDocumentType,
} from "@/domain/consentDocuments";
import { dateAsDayjs } from "@/utils/date";

export const useDownloadSignedConsentDocuments = () => {
  const [isDownloading, setIsDownloading] = useState(false);
  const recordDownloadEvent = useRecordDownloadEventMutation();

  const downloadSignedConsentDocuments = async (
    documents: SignedConsentDocumentType[],
  ) => {
    setIsDownloading(true);

    try {
      for (const document of documents) {
        const pdfBlob = await fetch(document.downloadUrl).then(r => r.blob());
        const pdfBytes = new Uint8Array(await pdfBlob.arrayBuffer());

        const representativeName = getRepresentativeName(document);

        const signedPdfBytes = await fillSignedConsentPdf({
          pdfBytes,
          patientSignatureDataUri: document.signatureUri,
          signatureBoxCoordinates: document.signatureBoxCoordinates,
          representativeSignatureDataUri:
            document.representativeSignatureDataUri,
          pcSignatureBoxCoordinates: document.pcSignatureBoxCoordinates,
          representativeName,
          pcNameCoordinates: document.pcNameCoordinates,
          patientName: {
            firstName: document.firstName,
            lastName: document.lastName,
          },
          patientInitialsCoordinates: document.patientInitialsCoordinates,
        });

        const blob = new Blob([signedPdfBytes.buffer as ArrayBuffer], {
          type: "application/pdf",
        });

        const url = URL.createObjectURL(blob);
        const link = window.document.createElement("a");

        link.href = url;
        link.download = `${dateAsDayjs().format("YYYY-MM-DD")}-consent-${document.type.toLowerCase()}-signed.pdf`;
        window.document.body.appendChild(link);
        link.click();
        window.document.body.removeChild(link);
        URL.revokeObjectURL(url);

        recordDownloadEvent.mutate({
          type: "CONSENT_DOCUMENT",
          id: document.id,
        });
      }
    } finally {
      setIsDownloading(false);
    }
  };

  return { downloadSignedConsentDocuments, isDownloading };
};
